<template>
  <div class="review-report-view">
    <section class="section dynamic-height" ref="form">
      <div class="columns is-multiline">
        <div class="column is-12">
          <div class="sticky-top" :class="hasShadow">
            <title-component
              v-if="kase.id"
              :size="1">
              <translate>Review case</translate>
              {{ kase.caseNumber }}
              <action-dropdown-component v-if="actions.length" :actions="actions" />
            </title-component>
          </div>
          <br>
          <div class="scrollable-content">
            <grid-details-component
              v-if="kase.id"
              :display_spec="caseSpecs"
              :item="kase"
              :item_keys="case_keys">
            </grid-details-component>
            <!-- Show Work proof review if the case is of type reporting -->
            <title-component
              v-if="reportingReviewResults.length"
              :size="2"
              :lineSeparator="'under'">
              <translate>Work proof</translate>
            </title-component>
            <review-report-grid-component
              v-if="reportingReviewResults.length"
              :reviewResults="reportingReviewResults"
              :columnClasses="'is-12-mobile is-12-tablet is-12-desktop'"
              v-on:openCommentSection="openCommentSection($event)"
              v-on:showFinding="showLatestFinding($event)">
            </review-report-grid-component>
            <!-- Show Customer review results if the case is of type entry -->
            <title-component
              v-if="customerReviewResults.length"
              :size="2"
              :lineSeparator="'under'">
              <translate>Customer</translate>
            </title-component>
            <review-report-grid-component
              v-if="customerReviewResults.length"
              :reviewResults="customerReviewResults"
              :columnClasses="'is-12-mobile is-6-tablet is-6-desktop'"
              v-on:openCommentSection="openCommentSection($event)"
              v-on:showFinding="showLatestFinding($event)">
            </review-report-grid-component>
            <!-- Show Customer if the case is of type Reporting or Renewal -->
            <template v-if="showCustomerInformation">
              <title-component
                :size="2"
                :lineSeparator="'under'">
                <translate>Customer</translate>
                <action-dropdown-component
                  v-if="customerActions.length"
                  :actions="customerActions">
                </action-dropdown-component>
              </title-component>
              <grid-details-component
                :display_spec="customerProfileSpecs"
                :item="kase.customer"
                :item_keys="customer_keys">
              </grid-details-component>
            </template>
            <!-- Show Certification list (case is of type: entry)-->
            <title-component
              v-if="serviceReviewResults.length"
              :size="2"
              :lineSeparator="'under'">
              <translate>Certification</translate>
            </title-component>
            <report-documents-component
              v-if="serviceDocumentsReviewResults.length"
              :reviewResults="serviceDocumentsReviewResults"
              v-on:openCommentSection="openCommentSection($event)"
              v-on:showFinding="showLatestFinding($event)">
            </report-documents-component>
            <br>
            <report-exam-component
              v-if="serviceExamReviewResults.length"
              :reviewResults="serviceExamReviewResults"
              :columnClasses="'is-12-mobile is-12-tablet is-12-desktop'"
              v-on:openCommentSection="openCommentSection($event)"
              v-on:showFinding="showLatestFinding($event)">
            </report-exam-component>

            <review-report-grid-component
              v-if="serviceOtherReviewResults.length"
              :reviewResults="serviceOtherReviewResults"
              :columnClasses="'is-12-mobile is-6-tablet is-6-desktop'"
              v-on:openCommentSection="openCommentSection($event)"
              v-on:showFinding="showLatestFinding($event)">
            </review-report-grid-component>

            <!-- Decisiton section title -->
            <title-component
              v-if="recommendation && recommendation.comment"
              :size="2"
              :lineSeparator="'under'">
              <translate>Decision</translate>
            </title-component>
            <!-- Message -->
            <message-component
              v-if="recommendation && recommendation.comment"
              :level="recommendation.isApproval ? 'success' : 'danger'">
              {{ recommendation.comment }}
            </message-component>
            <!-- Decision actions go here -->
            <slot name="decision-section"></slot>
          </div>
        </div>
      </div>
    </section>
    <section class="section dynamic-height">
      <div class="columns is-multilined">
        <div class="column is-12">
          <tabs-router-component class="route-tabs" :tabs="tabRoutes">
          </tabs-router-component>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
  import { mapActions, mapGetters, mapState } from 'vuex'
  import { caseSpecs, customerProfileSpecs } from '@/display_context/display_context'
  import TitleComponent from '@/components/layout/TitleComponent'
  import GridDetailsComponent from '@/components/layout/grid/GridDetailsComponent'
  import ReviewReportGridComponent from '@/components/layout/case_review/ReviewReportGridComponent'
  import ReportDocumentsComponent from '@/components/layout/case_report/ReportDocumentsComponent'
  import ReportExamComponent from '@/components/layout/case_report/ReportExamComponent'
  import { ReviewResult } from '@/classes/case/ReviewResult'
  import { itemRefresher, setPageHeightMixin, formHandlerMixin } from '@/mixins/mixins'
  import TabsRouterComponent from '@/components/layout/tabs/TabsRouterComponent.vue'
  import ActionDropdownComponent from '@/components/layout/utility/ActionDropdownComponent'
  import MessageComponent from '@/components/layout/utility/MessageComponent'

  export default {
    name: 'review-report-view',
    mixins: [setPageHeightMixin, itemRefresher, formHandlerMixin],
    data () {
      return {
        actions: [],
        case_keys: ['stateCell', 'created', 'service.code', 'qualification.display', 'reviewer.name', 'responsible.profileTypeDisplay', 'modified', 'caseType.display', 'controller.name', 'reviewCertificateLink'],
        customer_keys: ['firstName', 'lastName', 'personNumber', 'customerId', 'phoneNumber', 'mobileNumber', 'email', 'website', 'postalAddress', 'postalCode', 'city'],
        customerProfileSpecs: customerProfileSpecs,
        caseSpecs: caseSpecs,
        kase: {},
        customerActions: [],
        setAction: this.setCaseForReview,
        tabRoutes: [],
        recommendations: []
      }
    },
    props: {
      caseId: {
        required: true
      },
      routeFindingsName: {
        type: String,
        required: true
      },
      routeCommentsName: {
        type: String,
        required: true
      }
    },
    components: {
      TitleComponent,
      GridDetailsComponent,
      ReviewReportGridComponent,
      ReportDocumentsComponent,
      ReportExamComponent,
      TabsRouterComponent,
      ActionDropdownComponent,
      MessageComponent
    },
    computed: {
      ...mapState({
        cases: state => state.caseModule.cases,
        reviewResults: state => state.caseReviewModule.reviewResults,
        comments: state => state.commentModule.comments,
        authUser: state => state.authModule.authUser
      }),
      recommendation () {
        return this.recommendations.length && this.recommendations[this.recommendations.length - 1]
      },
      ...mapGetters([
        'groupedReviewResults',
        'getReviewResultsForType',
        'getFindingsForResponsible',
        'getReviewResultForId'
      ]),
      customerReviewResults () {
        let customerGroups = ['customer_invoice', 'customer_profile']
        return this.groupedReviewResults(customerGroups)
      },
      customerShowReviewResults () {
        return this.groupedReviewResults(['reporting', 'service_information'])
      },
      reportingReviewResults () {
        return this.groupedReviewResults(['reporting'])
      },
      showCustomerInformation () {
        if (this.kase.id) {
          return this.kase.isReportingCase() || this.kase.isRenewalCase()
        }
        return false
      },
      serviceReviewResults () {
        return this.groupedReviewResults('service_information')
      },
      serviceDocumentsReviewResults () {
        return this.getReviewResultsForType(this.groupedReviewResults('service_information'),
                                            [ReviewResult.FILE_TYPE, ReviewResult.MULTIPLE_FILES_TYPE])
      },
      serviceExamReviewResults () {
        return this.getReviewResultsForType(this.groupedReviewResults('service_information'), ReviewResult.RADIO_CHECKBOX_TYPE)
      },
      serviceOtherReviewResults () {
        return this.getReviewResultsForType(this.groupedReviewResults('service_information'), ReviewResult.OTHERS_TYPE)
      }
    },
    methods: {
      ...mapActions([
        'setCaseForReview',
        'getReviewResults',
        'clearReviewResults',
        'getComments',
        'clearReviewResultsFindings',
        'getRecommendationComment'
      ]),
      getCollapseItemTitle (result) {
        return `${result.getLabel()} (${result.finding.comments.length + 1})`
      },
      redirectToFindings () {
        this.$router.push({ name: this.routeFindingsName, params: { caseId: this.caseId } })
      },
      redirectToComments () {
        this.$router.push({ name: this.routeCommentsName, params: { caseId: this.caseId } })
      },
      openCommentSection (reviewResult) {
        this.redirectToComments()
        this.$nextTick(() => {
          // reset triggerToggle
          this.reviewResults.forEach((reviewResult) => {
            reviewResult.triggerToggle = false
          })
          reviewResult.triggerToggle = true
        })
      },
      showLatestFinding (reviewResult) {
        this.redirectToFindings()
        this.$nextTick(() => {
          // reset triggerToggle
          this.reviewResults.forEach((reviewResult) => {
            reviewResult.triggerToggle = false
          })
          reviewResult.triggerToggle = true
        })
      },
      initTabRoutes () {
        this.tabRoutes = [
          {
            route: { name: this.routeFindingsName },
            text: 'FINDINGS'
          },
          {
            route: { name: this.routeCommentsName },
            text: 'COMMENTS'
          }
        ]
      },
      isReviewer () {
        return this.kase.id && this.kase.isUserReviewer(this.authUser)
      },
      switchCaseKeys () {
        if (this.kase && this.kase.isOvertakingCase()) {
          this.case_keys.push('currentCa')
          this.case_keys.push('currentCertificateNumber')
        }
        if (this.kase && this.kase.isRenewalCase()) {
          this.case_keys.push('isCertificateReportedCell')
        }
      },
      getRecommendationMsg () {
        this.getRecommendationComment(this.caseId).then(recommendations => {
          this.recommendations = recommendations
        })
      },
      setActions () {
        if (this.isReviewer()) {
          this.actions = [{
            type: 'ACTION',
            func: () => {
              this.$router.push({ name: 'CaseReviewFindingsView', params: { caseId: this.caseId } })
            },
            text: 'Review case'
          }]
        }
      },
      setCustomerActions () {
        this.customerActions = [
          {
            type: 'ROUTE',
            route: { name: 'InvoicingView', params: { customerId: this.kase.customer.id } },
            text: 'View customer...'
          }
        ]
      }
    },
    created () {
      this.initTabRoutes()
      let callbacks = [this.setActions, this.switchCaseKeys, this.setCustomerActions, this.getRecommendationMsg]
      this.setOrRefreshItem('kase', this.cases, this.caseId, callbacks)
      this.getReviewResults(this.caseId).then(() => {
        // get the comments for all reviewResults and then adds them for each
        let requestData = {
          caseId: this.caseId
        }
        this.getComments(requestData).then(() => {
          for (let reviewResult of this.reviewResults) {
            reviewResult.resetComments()
          }
          for (let comment of this.comments) {
            let reviewResult = this.getReviewResultForId(comment.contextId)
            reviewResult && reviewResult.addComment(comment)
          }
        }).catch((err) => {
          this.handleError(err)
        })
      }).catch((err) => {
        this.handleError(err)
      })
    },
    beforeDestroy () {
      this.clearReviewResults()
      this.clearReviewResultsFindings()
    }
  }
</script>

<style lang="sass" scoped>
  .dynamic-height
    padding: 0
</style>
